import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import "./style.css";
import { API_ENDPOINTS } from "../../constants/apiEndpoints";
import { get } from "../../config/api";
import { useDispatch, useSelector } from "react-redux";
import { showError, showToast } from "../../store/slices/formconfig";
import { Loader } from "../loader";

interface BookletItem {
  booklet_id: number;
  book_start_no: number;
  book_end_no: number;
  status: string;
}

interface Props {
  show: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<boolean>;
  onDeliveryCodeSubmit: (deliveryCode: string) => Promise<void> | void;
  bookletData: BookletItem[];
  partnerData: any;
  isMH: string;
  show_govt_approval_tabFlow?: boolean;
  initialStep?: 1 | 2; // Accept active tab as parameter
  unitCode?: string;
}

const ApproveModal: React.FC<Props> = ({
  show,
  onClose,
  onSubmit,
  onDeliveryCodeSubmit,
  partnerData,
  isMH,
  show_govt_approval_tabFlow = false,
  initialStep = 1,
  unitCode,
}) => {
  const isIN57 = unitCode === 'IN57';
  // Step Management
  const [currentStep, setCurrentStep] = useState<1 | 2>(initialStep);

  // Step 1 State
  const currentYear = new Date().getFullYear();
  const defaultExpiryDate = new Date(Date.UTC(currentYear, 11, 31)).toISOString().split("T")[0];
  
  const [certificateNo, setCertificateNo] = useState("");
  const [expiryDate, setExpiryDate] = useState(defaultExpiryDate);
  const [noExpiry, setNoExpiry] = useState(false);
  const [firstPageFile, setFirstPageFile] = useState<File | null>(null);
  const [lastPageFile, setLastPageFile] = useState<File | null>(null);
  const [firstPagePreviewUrl, setFirstPagePreviewUrl] = useState<string | null>(null);
  const [lastPagePreviewUrl, setLastPagePreviewUrl] = useState<string | null>(null);
  const [certificateNoError, setCertificateNoError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmittingCode, setIsSubmittingCode] = useState(false);
  const [availableCertNumbers, setAvailableCertNumbers] = useState<number[]>([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState<number[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [bookletRanges, setBookletRanges] = useState<string>('');

  const suggestionsRef = useRef<HTMLDivElement>(null);
  const certInputRef = useRef<HTMLInputElement>(null);

  // Step 2 State (Delivery Code)
  const [codes, setCodes] = useState(['', '', '', '']);
  const codeRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];

  const userData = useSelector((state: any) => state.formConfig.user);
  const dispatch = useDispatch();

  const fetchMappedDistributors = useCallback(async () => {
    if (show_govt_approval_tabFlow) {
      return;
    }
    setIsLoadingData(true);
    if (!isMH) {
      try {
        const [numbersResponse, bookletsResponse] = await Promise.all([
          get(`${API_ENDPOINTS.FETCH_AVAILABLE_NUMBERS(userData.userId)}`),
          get(`${API_ENDPOINTS.ASSIGNABLE_CERTIFICATE_BOOKLETS_BY_USER(userData.userId)}`),
        ]);
        if (numbersResponse && numbersResponse.length > 0) {
          setAvailableCertNumbers(numbersResponse);
          setFilteredSuggestions(numbersResponse);
        }
        if (bookletsResponse && bookletsResponse.length > 0) {
          const ranges = bookletsResponse
            .map((b: any) => `${b.booklet_prefix}${b.book_start_no} - ${b.booklet_prefix}${b.book_end_no}`)
            .join(', ');
          setBookletRanges(ranges);
        }
      } catch (error) {
        dispatch(showError({ message: `Failed to fetch certificate numbers`, visible: true }));
      }
    }
    setIsLoadingData(false);
  }, [dispatch, show_govt_approval_tabFlow, isMH, userData?.userId]);

  useEffect(() => {
    if (show) {
      setCurrentStep(initialStep);
      if (isMH !== '') setCertificateNo(isMH);
      fetchMappedDistributors();
    }
  }, [fetchMappedDistributors, initialStep, isMH, show]);

  useEffect(() => {
    if (!firstPageFile || !firstPageFile.type.startsWith("image/")) {
      setFirstPagePreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(firstPageFile);
    setFirstPagePreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [firstPageFile]);

  useEffect(() => {
    if (!lastPageFile || !lastPageFile.type.startsWith("image/")) {
      setLastPagePreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(lastPageFile);
    setLastPagePreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [lastPageFile]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node) &&
        certInputRef.current && !certInputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCertificateNoChange = (value: string) => {
    setCertificateNo(value);
    const filtered = availableCertNumbers.filter((num) =>
      num.toString().toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
    setFilteredSuggestions(filtered);
    setShowSuggestions(true);
    if (value && availableCertNumbers.indexOf(Number(value)) === -1) {
      setCertificateNoError('Certificate number not found in assigned range');
    } else {
      setCertificateNoError(null);
    }
  };

  const handleSuggestionSelect = (num: number) => {
    setCertificateNo(num.toString());
    setCertificateNoError(null);
    setShowSuggestions(false);
  };

  const step1Valid = useMemo(() => {
    if (show_govt_approval_tabFlow) {
      return certificateNo !== "" && (expiryDate !== "" || noExpiry);
    }
    return (
      certificateNo !== "" &&
      (expiryDate !== "" || noExpiry) &&
      firstPageFile !== null &&
      lastPageFile !== null &&
      !certificateNoError
    );
  }, [certificateNo, expiryDate, noExpiry, firstPageFile, lastPageFile, certificateNoError, show_govt_approval_tabFlow]);

  const handleCodeChange = (val: string, index: number) => {
    if (/^\d*$/.test(val) && val.length <= 1) {
      const newCodes = [...codes];
      newCodes[index] = val;
      setCodes(newCodes);
      if (val.length === 1 && index < 3) codeRefs[index + 1].current?.focus();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: "first" | "last") => {
    const file = e.target.files?.[0];
    if (file && file.size <= 5 * 1024 * 1024) {
      type === "first" ? setFirstPageFile(file) : setLastPageFile(file);
    } else if (file) {
      dispatch(showToast({ message: "File size exceeds 5MB.", type: "error" }));
    }
  };

  const handleFileRemove = (type: "first" | "last") => {
    if (type === "first") {
      setFirstPageFile(null);
      return;
    }

    setLastPageFile(null);
  };

  const uploadFile = async (file: File) => {
    try {
      const res = await get(`${API_ENDPOINTS.CERTIFICATES_PRESIGNED_URL}?fileName=${file.name}&fileType=${file.type}&partnerId=${partnerData.partnerId}&request_id=${partnerData.requestId}`);
      await fetch(res.uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
      return res.fileUrl;
    } catch (error) {
      return null;
    }
  };

  const handleSaveNext = async () => {
    if (!step1Valid) return;

    if (show_govt_approval_tabFlow) {
      const success = await onSubmit({
        certificateNo,
        expiryDate: noExpiry ? null : expiryDate,
        firstPageUrl: null,
        lastPageUrl: null,
      });
      if (success) {
        onClose();
      }
      return;
    }

    setIsUploading(true);
    const [firstUrl, lastUrl] = await Promise.all([uploadFile(firstPageFile!), uploadFile(lastPageFile!)]);

    if (firstUrl && lastUrl) {
      const success = await onSubmit({
        certificateNo,
        expiryDate: noExpiry ? null : expiryDate,
        firstPageUrl: firstUrl,
        lastPageUrl: lastUrl,
      });
      setIsUploading(false);
      if (success) {
        if (isIN57) {
          onClose();
        } else {
          setCurrentStep(2);
        }
      }
    } else {
      setIsUploading(false);
    }
  };

  const handleFinalSubmit = async () => {
    const deliveryCode = codes.join('');
    if (deliveryCode.length < 4) return dispatch(showToast({ message: "Enter 4-digit code", type: "error" }));
    setIsSubmittingCode(true);
    await onDeliveryCodeSubmit(deliveryCode);
    setIsSubmittingCode(false);
  };

  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        {(isLoadingData || isUploading || isSubmittingCode) && <Loader />}
        <div className="modal-header-new">
          <div className="header-text-container">
            <h2 className="modal-title">{isMH || show_govt_approval_tabFlow ? 'Issue Certificate' : 'Deliver Certificate'}</h2>
            <p className="modal-subtitle">
              {show_govt_approval_tabFlow
                ? 'Fill in the certificate details to approve this request.'
                : 'Fill in the certificate details now. You can choose to deliver it to the retailer later.'}
            </p>
          </div>
          <span className="close-icon" onClick={onClose}>&times;</span>
        </div>

        {!isIN57 && !show_govt_approval_tabFlow && (
          <div className="stepper-container">
            <div className={`step-item ${currentStep >= 1 ? 'active' : ''}`}>
              <span className="step-number">{currentStep > 1 ? '✓' : '1'}</span>
              <span className="step-label">Enter Certificate Details</span>
            </div>
            <div className="step-line"></div>
            <div className={`step-item ${currentStep === 2 ? 'active' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">Enter Delivery Code</span>
            </div>
          </div>
        )}

        <div className="modal-body" style={{padding: '10px 25px'}}>
          {currentStep === 1 ? (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label className="subhead">Certificate no. *</label>
                  <div className="cert-autocomplete-wrapper">
                    <input
                      ref={certInputRef}
                      type="text"
                      value={certificateNo}
                      disabled={show_govt_approval_tabFlow || !!isMH || (!isMH && !isLoadingData && availableCertNumbers.length === 0)}
                      onChange={(e) => handleCertificateNoChange(e.target.value)}
                      onFocus={() => setShowSuggestions(true)}
                      placeholder="Select from assigned range"
                      autoComplete="off"
                      style={!isMH && !isLoadingData && availableCertNumbers.length === 0 ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
                    />
                    {showSuggestions && !isMH && !show_govt_approval_tabFlow && certificateNo && (
                      <div className="cert-suggestions-dropdown" ref={suggestionsRef}>
                        {filteredSuggestions.length > 0 ? (
                          filteredSuggestions.map((num) => (
                            <div
                              key={num}
                              className="cert-suggestion-item"
                              onMouseDown={() => handleSuggestionSelect(num)}
                            >
                              {num}
                            </div>
                          ))
                        ) : (
                          <div className="cert-suggestion-item no-result">No certificate found</div>
                        )}
                      </div>
                    )}
                  </div>
                  {!isMH && !isLoadingData && !show_govt_approval_tabFlow && availableCertNumbers.length === 0 && (
                    <p style={{ color: 'red', fontSize: 12, margin: '4px 0 0' }}>No certificates available. Reach out to your commercial manager.</p>
                  )}
                  {!isMH && !isLoadingData && !show_govt_approval_tabFlow && bookletRanges && (
                    <p style={{ fontSize: 10, margin: '4px 0 0', color: '#555' }}>Available range: {bookletRanges}</p>
                  )}

                </div>
                <div className="form-group">
                  <label className="subhead">Certificate Expiry Date *</label>
                  <div className="date-input-wrapper">
                    <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} disabled={noExpiry} />
                  </div>
                  <div className="checkbox-group">
                    <input type="checkbox" id="noExpiry" checked={noExpiry} onChange={(e) => { setNoExpiry(e.target.checked); if(e.target.checked) setExpiryDate(""); }} style={{width:'auto'}} />
                    <label htmlFor="noExpiry">No Expiry Date</label>
                  </div>
                </div>
              </div>

              {!show_govt_approval_tabFlow && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="subhead">Certificate First Page *</label>
                    <label
                      className={`upload-card ${firstPageFile ? 'has-file' : ''} ${firstPagePreviewUrl ? 'has-image-preview' : ''}`}
                      style={firstPagePreviewUrl ? { backgroundImage: `linear-gradient(rgba(12, 23, 33, 0.28), rgba(12, 23, 33, 0.28)), url(${firstPagePreviewUrl})` } : undefined}
                    >
                      <input type="file" onChange={(e) => handleFileChange(e, "first")} accept=".jpg,.png,.pdf" hidden />
                      {firstPageFile && (
                        <button
                          type="button"
                          className="upload-remove-button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleFileRemove("first");
                          }}
                          aria-label="Remove first page file"
                        >
                          ×
                        </button>
                      )}
                      <div className="upload-content">
                        <span className="upload-icon">📄</span>
                        <p className="upload-text">{firstPageFile ? firstPageFile.name : "Click here to upload"}</p>
                        <p className="upload-hint">JPG, PNG or PDF (max. 5 MB)</p>
                      </div>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="subhead">Certificate Last Page *</label>
                    <label
                      className={`upload-card ${lastPageFile ? 'has-file' : ''} ${lastPagePreviewUrl ? 'has-image-preview' : ''}`}
                      style={lastPagePreviewUrl ? { backgroundImage: `linear-gradient(rgba(12, 23, 33, 0.28), rgba(12, 23, 33, 0.28)), url(${lastPagePreviewUrl})` } : undefined}
                    >
                      <input type="file" onChange={(e) => handleFileChange(e, "last")} accept=".jpg,.png,.pdf" hidden />
                      {lastPageFile && (
                        <button
                          type="button"
                          className="upload-remove-button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleFileRemove("last");
                          }}
                          aria-label="Remove last page file"
                        >
                          ×
                        </button>
                      )}
                      <div className="upload-content">
                        <span className="upload-icon">📄</span>
                        <p className="upload-text">{lastPageFile ? lastPageFile.name : "Click here to upload"}</p>
                        <p className="upload-hint">JPG, PNG or PDF (max. 5 MB)</p>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="delivery-step-content">
              <label className="subhead">Enter 4 digit delivery code *</label>
              <div className="otp-container">
                {codes.map((code, i) => (
                  <input key={i} ref={codeRefs[i]} type="text" maxLength={1} value={code} className="otp-input" onChange={(e) => handleCodeChange(e.target.value, i)} onKeyDown={(e) => { if (e.key === 'Backspace' && !codes[i] && i > 0) codeRefs[i - 1].current?.focus(); }} />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {currentStep === 1 ? (
            <button className={`btn-primary ${!step1Valid || isUploading ? 'disabled' : ''}`} onClick={handleSaveNext} disabled={!step1Valid || isUploading}>{isUploading ? 'Submitting...' : (show_govt_approval_tabFlow || isIN57 ? 'Submit' : 'Save & Next')}</button>
          ) : (
            <button className="btn-primary" onClick={handleFinalSubmit} disabled={isSubmittingCode}>{isSubmittingCode ? 'Submitting...' : 'Submit'}</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApproveModal;